//CSV Upload Feature

import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  IconButton,
  Chip,
  Paper,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { uploadFileToBlob } from "../config/apiService";

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// Expected CSV schema
const EXPECTED_CSV_HEADERS = [
  "Discom",
  "Cluster",
  "Zone",
  "Circle",
  "Division",
  "SubDivision",
  "SubStation",
  "Feeder Code",
  "Feeder Name",
  "DTR Code",
  "DTR Name",
  "Consumer_Name",
  "Consumer_Number",
  "newMeterNumber",
  "Meter Type",
  "Communication Status",
  "Last Response Date",
];

// Parse CSV header row
const parseCSVHeaders = (headerLine) => {
  if (!headerLine) return [];

  const cleanHeaderLine = headerLine.replace(/^\uFEFF/, "");
  const headers = [];
  let currentHeader = "";
  let insideQuotes = false;

  for (let i = 0; i < cleanHeaderLine.length; i++) {
    const char = cleanHeaderLine[i];

    if (char === '"') {
      if (
        insideQuotes &&
        cleanHeaderLine[i + 1] === '"'
      ) {
        currentHeader += '"';
        i++;
      } else {
        insideQuotes = !insideQuotes;
      }
    } else if (char === "," && !insideQuotes) {
      headers.push(currentHeader);
      currentHeader = "";
    } else {
      currentHeader += char;
    }
  }

  headers.push(currentHeader);

  return headers;
};

// Validate CSV schema
const validateCSVSchema = (headers) => {
  const missingColumns = EXPECTED_CSV_HEADERS.filter(
    (expectedHeader) => !headers.includes(expectedHeader)
  );

  const unexpectedColumns = headers.filter(
    (uploadedHeader) => !EXPECTED_CSV_HEADERS.includes(uploadedHeader)
  );

  return {
    isValid:
      missingColumns.length === 0 &&
      unexpectedColumns.length === 0 &&
      headers.length === EXPECTED_CSV_HEADERS.length,
    missingColumns,
    unexpectedColumns,
  };
};

// Read CSV header
const readCSVHeaders = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const blob = file.slice(0, 64 * 1024);

    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const firstLine = text.split(/\r?\n/)[0];
        const headers = parseCSVHeaders(firstLine);
        resolve(headers);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error("Unable to read the CSV file."));
    };

    reader.readAsText(blob, "UTF-8");
  });
};

const FileUploadModal = ({ open, onClose }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [successResult, setSuccessResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [validatingSchema, setValidatingSchema] = useState(false);

  const fileInputRef = useRef(null);

  // Reset states when modal opens
  useEffect(() => {
    if (open) {
      setSelectedFile(null);
      setUploadProgress(0);
      setSuccessResult(null);
      setErrorMessage(null);
      setValidatingSchema(false);
      setIsDragging(false);
    }
  }, [open]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const validateAndSetFile = async (file) => {
    if (!file) return;

    const allowedExtensions = [".csv"];
    const ext = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();

    if (!allowedExtensions.includes(ext)) {
      setErrorMessage("Only CSV (.csv) files are allowed.");
      setSelectedFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      return;
    }

    setErrorMessage(null);
    setSuccessResult(null);
    setValidatingSchema(true);

    try {
      const headers = await readCSVHeaders(file);
      const validationResult = validateCSVSchema(headers);

      if (!validationResult.isValid) {
        let message =
          "File upload failed. Please check the CSV schema.";

        if (validationResult.missingColumns.length > 0) {
          message +=
            `\nMissing columns: ${validationResult.missingColumns.join(
              ", "
            )}`;
        }

        if (validationResult.unexpectedColumns.length > 0) {
          message +=
            `\nUnexpected columns: ${validationResult.unexpectedColumns.join(
              ", "
            )}`;
        }

        setErrorMessage(message);
        setSelectedFile(null);

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        return;
      }

      setSelectedFile(file);
      setErrorMessage(null);
    } catch (error) {
      console.error("CSV schema validation error:", error);

      setSelectedFile(null);
      setErrorMessage(
        "File upload failed. Please check the CSV schema."
      );

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } finally {
      setValidatingSchema(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || validatingSchema) return;

    setUploading(true);
    setUploadProgress(0);
    setErrorMessage(null);
    setSuccessResult(null);

    try {
      const response = await uploadFileToBlob(
        selectedFile,
        (progress) => setUploadProgress(progress)
      );

      setSuccessResult(response);
      setSelectedFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (err) {
      console.error("Upload error:", err);
      const msg =
        err.response?.data?.error ||
        err.message ||
        "An error occurred while uploading the file. Please try again.";
      setErrorMessage(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading && !validatingSchema) {
      onClose();
    }
  };

  const isCsv =
    selectedFile &&
    selectedFile.name.toLowerCase().endsWith(".csv");

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1,
          boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              bgcolor: "#e3f2fd",
              color: "#1976d2",
              p: 1,
              borderRadius: 2,
              display: "flex",
            }}
          >
            <CloudUploadIcon fontSize="medium" />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={600} color="#1e293b">
              Upload CSV File
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Upload your data file for processing
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={handleClose}
          disabled={uploading || validatingSchema}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2.5, pb: 2.5 }}>
        {/* Dropzone */}
        {!selectedFile && !validatingSchema && (
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            sx={{
              border: "2px dashed",
              borderColor: isDragging ? "#1976d2" : "#cbd5e1",
              borderRadius: 3,
              p: 4,
              textAlign: "center",
              bgcolor: isDragging ? "rgba(25, 118, 210, 0.05)" : "#fafafa",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                borderColor: "#1976d2",
                bgcolor: "rgba(25, 118, 210, 0.03)",
              },
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 48, color: "#94a3b8", mb: 1 }} />
            <Typography variant="body1" fontWeight={600} color="#334155">
              Drag & Drop your file here, or{" "}
              <span style={{ color: "#1976d2", textDecoration: "underline" }}>
                Browse
              </span>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Supports CSV (.csv) files (up to 100MB)
            </Typography>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".csv"
              style={{ display: "none" }}
            />
          </Box>
        )}

        {/* Schema Validation */}
        {validatingSchema && (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: "#f8fafc",
              border: "1px solid #e2e8f0",
              textAlign: "center",
            }}
          >
            <CircularProgress size={30} sx={{ mb: 1.5 }} />

            <Typography
              variant="body1"
              fontWeight={600}
              color="#334155"
            >
              Validating CSV schema...
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              Please wait while we check the column names.
            </Typography>
          </Paper>
        )}

        {/* Selected File Card */}
        {selectedFile && (
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: 2,
              bgcolor: "#f8fafc",
              border: "1px solid #e2e8f0",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    bgcolor: "#e0e7ff",
                    color: "#4338ca",
                    p: 1,
                    borderRadius: 2,
                    display: "flex",
                  }}
                >
                  <InsertDriveFileIcon />
                </Box>
                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    noWrap
                  >
                    {selectedFile.name}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.3 }}>
                    <Chip
                      label={isCsv ? "CSV" : "FILE"}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        bgcolor: "#c7d2fe",
                        color: "#3730a3",
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {formatBytes(selectedFile.size)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {!uploading && (
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedFile(null);
                    setErrorMessage(null);

                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                    }
                  }}
                  sx={{ color: "#ef4444" }}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              )}
            </Box>
          </Paper>
        )}

        {/* Upload Progress */}
        {uploading && (
          <Box sx={{ mt: 2.5 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0.8,
              }}
            >
              <Typography variant="body2" fontWeight={500} color="text.secondary">
                Uploading file...
              </Typography>
              <Typography variant="body2" fontWeight={600} color="#1976d2">
                {uploadProgress}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={uploadProgress}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        )}

        {/* Success Alert */}
        {successResult && (
          <Alert
            severity="success"
            icon={<CheckCircleOutlineIcon fontSize="inherit" />}
            sx={{ mt: 2, borderRadius: 2 }}
          >
            <Typography variant="subtitle2" fontWeight={600}>
              File "{successResult.fileName}" uploaded successfully!
            </Typography>
            {successResult.size && (
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                Size: {formatBytes(successResult.size)}
              </Typography>
            )}
          </Alert>
        )}

        {/* Error Alert */}
        {errorMessage && (
          <Alert
            severity="error"
            sx={{
              mt: 2,
              borderRadius: 2,
              whiteSpace: "pre-line",
            }}
          >
            <Typography variant="body2">
              {errorMessage}
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          disabled={uploading || validatingSchema}
          variant="outlined"
          color="inherit"
          sx={{ borderRadius: 2 }}
        >
          {successResult ? "Close" : "Cancel"}
        </Button>
        {successResult ? (
          <Button
            onClick={() => {
              setSuccessResult(null);
              setSelectedFile(null);
              setErrorMessage(null);

              if (fileInputRef.current) {
                fileInputRef.current.value = "";
              }
            }}
            variant="contained"
            color="primary"
            sx={{ borderRadius: 2 }}
          >
            Upload Another File
          </Button>
        ) : (
          <Button
            onClick={handleUpload}
            disabled={
              !selectedFile ||
              uploading ||
              validatingSchema
            }
            variant="contained"
            color="primary"
            startIcon={
              uploading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />
            }
            sx={{ borderRadius: 2, px: 3 }}
          >
            {uploading ? "Uploading..." : "Upload File"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FileUploadModal;